import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from 'react-google-autocomplete';
import { StanButton, StanInput } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { type AddressFormData, addressSchema } from '@/features/email-flows/validations/address';
import { parseGooglePlaceAddress } from '@/features/email-flows/logic/emailFlows.logic';
import { configSettings } from '@/lib/config';
import styles from '@/components/core-components/StanInput/StanInput.module.css';
import { cn } from '@/lib/utils/cn';
import { useAddress } from '../../queries/useAddress';

// Type for Google Places API
interface PlaceResult {
    address_components?: Array<{
        long_name: string;
        short_name: string;
        types: string[];
    }>;
}

export const AddressSection: React.FC = () => {
    const { user } = useUser();
    const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
    const [isFocused, setIsFocused] = useState(false);
    const { mutateAsync: updateAddressMutation, isPending: isSubmitting } = useAddress();

    // Validate Google Maps API key
    const apiKey = configSettings.googleMapsApiKey;
    if (!apiKey) {
        console.error('Google Maps API key is not configured');
    }

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
        watch,
        setValue,
    } = useForm<AddressFormData>({
        resolver: zodResolver(addressSchema),
        defaultValues: {
            street: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
        },
    });

    const watchedValues = watch();

    // Handle Google Places autocomplete selection
    const handlePlaceSelected = (place: PlaceResult) => {
        if (!place.address_components) {
            console.error('Google Places API returned incomplete data');
            return;
        }

        const parsedAddress = parseGooglePlaceAddress(place.address_components);

        // Update form values using setValue
        setValue('street', parsedAddress.street, { shouldValidate: true });
        setValue('city', parsedAddress.city, { shouldValidate: true });
        setValue('state', parsedAddress.state, { shouldValidate: true });
        setValue('postal_code', parsedAddress.postal_code, { shouldValidate: true });
        setValue('country', parsedAddress.country, { shouldValidate: true });
    };

    // Initialize form data from user
    useEffect(() => {
        if (user) {
            reset({
                street: user.data.address?.street || '',
                city: user.data.address?.city || '',
                state: user.data.address?.state || '',
                postal_code: user.data.address?.postal_code || '',
                country: user.data.address?.country || '',
            });
        }
    }, [user, reset]);

    const isButtonDisabled = isSubmitting || !isDirty;

    const onSubmit = async (data: AddressFormData) => {
        setFormErrors({});

        try {
            // Call the settings API
            await updateAddressMutation(data);

            // Analytics
            stanAnalytics('address-update', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
                props: {
                    update_type: 'address_change',
                    fields_updated: Object.keys(data).filter(key => data[key as keyof AddressFormData]),
                },
            });

            stanNotify({
                type: 'success',
                title: 'Address Updated!',
                text: 'Your address has been updated successfully.',
            });
        } catch (error: any) {
            setFormErrors(error.response?.data?.errors || {});
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: error?.response?.data?.message || 'Please try again later.',
            });
            console.error('Address update error:', error);
        }
    };

    return (
        <section className='w-full max-w-3xl space-y-6'>
            <h4>Address</h4>

            <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <Controller
                        name='street'
                        control={control}
                        render={({ field }) => (
                            <div
                                className={cn(
                                    'stan-input',
                                    (errors.street || formErrors.street) && styles.negative,
                                    isSubmitting && styles.disabled
                                )}
                            >
                                <label className={styles.label} style={{ marginBottom: '0.5rem' }}>
                                    Street Address
                                </label>
                                <div
                                    className={cn('p-0!', styles.wrapper, isFocused && styles.active)}
                                    onMouseEnter={e => {
                                        if (!isSubmitting) {
                                            e.currentTarget.style.borderColor = 'var(--bg-color-primary-brand-main)';
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!isFocused && !errors.street && !formErrors.street) {
                                            e.currentTarget.style.borderColor = 'var(--border-color-disabled)';
                                        }
                                    }}
                                >
                                    <Autocomplete
                                        apiKey={configSettings.googleMapsApiKey}
                                        onPlaceSelected={handlePlaceSelected}
                                        options={{
                                            types: ['address'],
                                        }}
                                        className={cn('h-full px-4 py-3', styles.field)}
                                        placeholder='Start typing your address...'
                                        value={field.value}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                            field.onChange(e.target.value)
                                        }
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        disabled={isSubmitting}
                                        autoComplete='off'
                                        aria-label='Street Address'
                                        aria-describedby='street-address-hint'
                                    />
                                </div>
                                {(errors.street || formErrors.street) && (
                                    <span className={styles.hint}>
                                        {errors.street?.message || formErrors.street?.[0]}
                                    </span>
                                )}
                            </div>
                        )}
                    />

                    <div>
                        <StanInput
                            {...register('city')}
                            name='city'
                            value={watchedValues.city || ''}
                            topLabel='City'
                            error={!!errors.city || !!formErrors.city}
                            hintText={errors.city?.message || formErrors.city?.[0]}
                        />
                    </div>
                </div>

                <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                    <div>
                        <StanInput
                            {...register('state')}
                            name='state'
                            value={watchedValues.state || ''}
                            topLabel='State/Province'
                            error={!!errors.state || !!formErrors.state}
                            hintText={errors.state?.message || formErrors.state?.[0]}
                        />
                    </div>

                    <div>
                        <StanInput
                            {...register('postal_code')}
                            name='postal_code'
                            value={watchedValues.postal_code || ''}
                            topLabel='Postal Code'
                            error={!!errors.postal_code || !!formErrors.postal_code}
                            hintText={errors.postal_code?.message || formErrors.postal_code?.[0]}
                        />
                    </div>
                </div>

                <div className='grid grid-cols-1 gap-6'>
                    <div>
                        <StanInput
                            {...register('country')}
                            name='country'
                            value={watchedValues.country || ''}
                            topLabel='Country'
                            error={!!errors.country || !!formErrors.country}
                            hintText={errors.country?.message || formErrors.country?.[0]}
                        />
                    </div>
                </div>

                <div className='flex items-center gap-4'>
                    <StanButton
                        name='settings-address-update-button'
                        type='submit'
                        disabled={isButtonDisabled}
                        loading={isSubmitting}
                    >
                        Update
                    </StanButton>

                    {isSubmitting && (
                        <div className='text-caption-1-regular flex items-center'>
                            <div className='mr-2 size-4 animate-spin rounded-full border-b-2' />
                            Updating...
                        </div>
                    )}
                </div>
            </form>
        </section>
    );
};
